import { MAX_CHARACTERS_FOR_DISPLAY, TRADABLE_TYPE } from "@helpers/constants";
import { AssetInfo } from "@models/tradable/split/asset_info";
import { TradableStatic } from "@models/tradable/split/tradable_static";
import { AssetSettings } from "@models/tradable/split/asset_settings";
import { AssetPrice } from "@models/tradable/split/asset_price";
import { TradableDetailInterface, TradableDetailType } from "@models/interfaces/tradable_detail_types";

export abstract class Tradable extends TradableStatic {
	// Low-volatile data
	protected assetSettings: AssetSettings = null;

	// High-volatile data
	protected assetPrice: AssetPrice = null;

	// Translations
	protected assetInfo: AssetInfo = null;

	// Internal / Utility
	protected static readonly currencyType: string = TRADABLE_TYPE;
	protected readonly tradable_detail_fields: string[] = ["total_return", "change24h_return", "avg_buy_price"];
	protected readonly tradable_actions: string[] = ["buy", "sell", "swap", "transfer", "savings"];

	public readonly tradableDetailLabels: TradableDetailInterface[] = [
		{
			label: "daily_high",
			translationKey: "tradable_detail_label_daily_high",
			type: TradableDetailType.FIAT
		},
		{
			label: "daily_low",
			translationKey: "tradable_detail_label_daily_low",
			type: TradableDetailType.FIAT
		},
		{
			label: "monthly_volatility",
			translationKey: "tradable_detail_label_monthly_volatility",
			type: TradableDetailType.PERCENTAGE
		},
		{
			label: "high_52_w",
			translationKey: "tradable_detail_label_high_52_w",
			type: TradableDetailType.FIAT
		},
		{
			label: "low_52_w",
			translationKey: "tradable_detail_label_low_52_w",
			type: TradableDetailType.FIAT
		},
		{
			label: "market_cap",
			translationKey: "tradable_detail_label_market_cap",
			type: TradableDetailType.FIAT
		}
	];

	// Abstracts
	public abstract isTransferActive(): boolean;
	public abstract isWithdrawActive(): boolean;
	public abstract isDepositActive(): boolean;
	public abstract isHodlTradable(type: string): boolean;
	public abstract isIota(): boolean;
	public abstract isStellar(): boolean;

	/*
		Socket (high-volatile) prices fields
		- Backend can't update the Socket Rooms with the new structure in useful time. This will be a future iteration update.
		- So we still need to use the old properties for the Prices (high-volatile) fields to be able to update the new models on socket events.
	 */
	protected avg_price: string = "";
	// Changed amount in percent
	protected change_24h: string = "";
	protected change_1w: string = "";
	protected change_1m: string = "";
	protected change_6m: string = "";
	protected change_1y: string = "";
	protected change_5y: string = "";
	// Changed amount in euro
	protected change_24h_amount: string = "";
	protected change_1w_amount: string = "";
	protected change_1m_amount: string = "";
	protected change_6m_amount: string = "";
	protected change_1y_amount: string = "";
	protected change_5y_amount: string = "";

	/*
		Low volatile methods:
	*/

	public getAssetSettings(): AssetSettings {
		return this.assetSettings;
	}

	public setAssetSettings(s: AssetSettings): void {
		this.assetSettings = s;
	}

	public getMinBuy(): number {
		return this.getAssetSettings()?.getMinBuy() ?? 0;
	}

	public getMinSell(): number {
		return this.getAssetSettings()?.getMinSell() ?? 0;
	}

	public getSort(): number {
		return this.getAssetSettings()?.getSort() ?? 0;
	}

	public isAvailable(): boolean {
		return this.getAssetSettings()?.isAvailable() ?? false;
	}

	public isBuyActive(): boolean {
		return this.getAssetSettings()?.isBuyActive() ?? false;
	}

	public isIndexOnly(): boolean {
		return this.getAssetSettings()?.isIndexOnly() ?? false;
	}

	public isMaintenance(): boolean {
		return this.getAssetSettings()?.isMaintenance() ?? false;
	}

	public isSellActive(): boolean {
		return this.getAssetSettings()?.isSellActive() ?? false;
	}

	/*
		High volatile methods:
	*/

	public getAssetPrices(): AssetPrice {
		return this.assetPrice;
	}

	public setAssetPrices(s: AssetPrice): void {
		this.assetPrice = s;
	}

	public getCirculatingSupply(): number {
		return this.getAssetPrices()?.getCirculatingSupply() ?? 0;
	}

	public getPrice(): number {
		return this.getAssetPrices()?.getPrice() ?? 0;
	}

	public getAvgPrice(): number {
		return this.getPrice();
	}

	// Percent
	public getChange24h(): number {
		return this.getAssetPrices()?.getChange24h() ?? 0;
	}

	public getChange1w(): number {
		return this.getAssetPrices()?.getChange1w() ?? 0;
	}

	public getChange1m(): number {
		return this.getAssetPrices()?.getChange1m() ?? 0;
	}

	public getChange6m(): number {
		return this.getAssetPrices()?.getChange6m() ?? 0;
	}

	public getChange1y(): number {
		return this.getAssetPrices()?.getChange1y() ?? 0;
	}

	public getChange5y(): number {
		return this.getAssetPrices()?.getChange5y() ?? 0;
	}

	// Amount
	public getChange24hAmount(): number {
		return this.getAssetPrices()?.getChange24hAmount() ?? 0;
	}

	public getChange1wAmount(): number {
		return this.getAssetPrices()?.getChange1wAmount() ?? 0;
	}

	public getChange1mAmount(): number {
		return this.getAssetPrices()?.getChange1mAmount() ?? 0;
	}

	public getChange6mAmount(): number {
		return this.getAssetPrices()?.getChange6mAmount() ?? 0;
	}

	public getChange1yAmount(): number {
		return this.getAssetPrices()?.getChange1yAmount() ?? 0;
	}

	public getChange5yAmount(): number {
		return this.getAssetPrices()?.getChange5yAmount() ?? 0;
	}

	// Socket data (old attributes) - we still need to parse them on socket events only
	public getSocketAvgPrice(): string {
		return this.avg_price;
	}

	public getSocketChange24h(): number {
		return +this.change_24h;
	}

	public getSocketChange1w(): number {
		return +this.change_1w;
	}

	public getSocketChange1m(): number {
		return +this.change_1m;
	}

	public getSocketChange6m(): number {
		return +this.change_6m;
	}

	public getSocketChange1y(): number {
		return +this.change_1y;
	}

	public getSocketChange5y(): number {
		return +this.change_5y;
	}

	public getSocketChange24hAmount(): number {
		return +this.change_24h_amount;
	}

	public getSocketChange1wAmount(): number {
		return +this.change_1w_amount;
	}

	public getSocketChange1mAmount(): number {
		return +this.change_1m_amount;
	}

	public getSocketChange6mAmount(): number {
		return +this.change_6m_amount;
	}

	public getSocketChange1yAmount(): number {
		return +this.change_1y_amount;
	}

	public getSocketChange5yAmount(): number {
		return +this.change_5y_amount;
	}

	/*
		Internal / Utility methods:
	*/

	public getCurrencyType(): string {
		return Tradable.currencyType;
	}

	public getDetailFields(): string[] {
		return this.tradable_detail_fields;
	}

	public getIconCssName(lc: boolean = false): string {
		return "bpi__asset-" + this.getSymbol(lc);
	}

	public getPrecisionForTx(): number {
		return 8;
	}

	public getTradableActions(): string[] {
		return this.tradable_actions;
	}

	public hasLengthyName(): boolean {
		return this.getName().length > MAX_CHARACTERS_FOR_DISPLAY;
	}

	public isDerivative(): boolean {
		return false;
	}

	/*
		Translations
	*/

	public getAssetInfo(): AssetInfo {
		return this.assetInfo;
	}

	public setAssetInfo(a: AssetInfo): void {
		this.assetInfo = a;
	}

	public getInfo(): string {
		return this.getAssetInfo()?.getInfo() ?? "";
	}

	public hasInfo(): boolean {
		return this.getInfo().trim().length > 0;
	}

	public getDescription(): string {
		return this.getAssetInfo()?.getDescription() ?? "";
	}

	public getExtraInfo(): string {
		return this.getAssetInfo()?.getExtraInfo() ?? "";
	}

	public getInfoIntegration(): string {
		return this.getAssetInfo()?.getInfoIntegration() ?? "";
	}
}
